import { createFileRoute, useLocation } from "@tanstack/react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Award, Code, Users, BookOpen } from "lucide-react"
import { SiGithub } from "react-icons/si"
import { FaLinkedin } from "react-icons/fa6"
import { SEO } from "@/components/SEO"
import { StructuredData } from "@/components/StructuredData"
import { aboutPage, siteMetadata } from "@/content"

export const Route = createFileRoute("/about")({
  component: AboutPage,
})

const iconMap = {
  award: Award,
  code: Code,
  users: Users,
  bookOpen: BookOpen,
  github: SiGithub,
  linkedin: FaLinkedin,
}

function AboutPage() {
  const location = useLocation()
  const MissionIcon = iconMap[aboutPage.mission.icon as keyof typeof iconMap]
  const LeadershipIcon = iconMap[aboutPage.leadership.icon as keyof typeof iconMap]
  const CapabilitiesIcon = iconMap[aboutPage.capabilities.icon as keyof typeof iconMap]

  return (
    <>
      <SEO metadata={aboutPage.seo} />
      <StructuredData type="about" pageTitle="About SapientsAI" pageUrl={`${siteMetadata.url}${location.pathname}`} />
      <div className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">{aboutPage.header.title}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{aboutPage.header.subtitle}</p>
          </div>

          {/* Company Mission */}
          <Card className="mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                <MissionIcon className="h-6 w-6 text-primary" />
                {aboutPage.mission.title}
              </h2>
              <div className="space-y-4 text-muted-foreground">
                {aboutPage.mission.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Leadership */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <LeadershipIcon className="h-6 w-6 text-primary" />
                  {aboutPage.leadership.title}
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  {aboutPage.leadership.content.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Our Capabilities */}
            <Card>
              <CardContent className="p-8">
                <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                  <CapabilitiesIcon className="h-6 w-6 text-primary" />
                  {aboutPage.capabilities.title}
                </h2>
                <div className="space-y-4">
                  {aboutPage.capabilities.sections.map((section, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-lg mb-2">{section.title}</h3>
                      <p className="text-muted-foreground">{section.content}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Connect */}
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-6">{aboutPage.connect.title}</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">{aboutPage.connect.subtitle}</p>
            <div className="flex justify-center gap-6">
              {aboutPage.connect.links.map((link, index) => {
                const LinkIcon = iconMap[link.icon as keyof typeof iconMap]
                return (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-accent transition-colors"
                  >
                    <LinkIcon className="h-5 w-5" />
                    {link.platform}
                  </a>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
